import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAssessmentReport,
  postAssessmentReport,
  postEpicAction,
  type AssessmentReportResult,
} from '../../../../lib/api/epics';
import type { EpicAction, EpicDetail } from '../../../../../../shared/types/epics';
import { Button, ConfirmDialog as SharedConfirmDialog } from '../../../../components/ui';

// ---------------------------------------------------------------------------
// Operator actions (FR-8) — Retry / Proceed / Abort stay directly visible (the
// primary lifecycle actions); anything secondary (the migration/safety-net
// assessment report) moves into a "More ▾" menu — UX-05 §7, same role="menu"
// pattern as Header.tsx's NavDropdown.
// ---------------------------------------------------------------------------

export const ActionsBar: React.FC<{ epic: EpicDetail }> = ({ epic }) => {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<{ url: string; action: EpicAction } | null>(null);
  const [confirmAction, setConfirmAction] = useState<EpicAction | null>(null);
  const [reportResult, setReportResult] = useState<AssessmentReportResult | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement | null>(null);
  const isReportableEpic = epic.epicKind === 'migration' || epic.epicKind === 'safety-net';

  // On load, silently check whether a report already exists for this epic
  // (GET, no Lambda re-invoke) so "download PDF / view HTML" shows up
  // immediately without the operator having to click Generate again just to
  // get a link — the report itself lives in S3 for 30 days, only the
  // presigned link (15 min) actually expires. A miss (null) just means
  // nothing's been generated yet; the button below covers that case as before.
  const existingReportQuery = useQuery({
    queryKey: ['assessment-report', String(epic.id)],
    queryFn: () => getAssessmentReport(epic.id),
    enabled: isReportableEpic,
    staleTime: 0,
    retry: false,
  });
  useEffect(() => {
    if (existingReportQuery.data) setReportResult(existingReportQuery.data);
  }, [existingReportQuery.data]);

  // "Generate PDF Report" — migration/safety-net epics only. Can take up to
  // ~100s on a cold run (headless-Chromium launch server-side), so the button
  // shows a spinner rather than optimistically assuming it's fast.
  const reportMutation = useMutation({
    mutationFn: () => postAssessmentReport(epic.id),
    onSuccess: (result) => setReportResult(result),
  });

  // "Refresh link" — once a report exists, get a new presigned URL without
  // re-rendering anything (cheap, instant). Covers the case where the
  // operator comes back after the last link's 15-minute window closed.
  const refreshLinkMutation = useMutation({
    mutationFn: () => getAssessmentReport(epic.id),
    onSuccess: (result) => { if (result) setReportResult(result); },
  });

  const mutation = useMutation({
    mutationFn: (action: EpicAction) =>
      postEpicAction(epic.id, { action }),
    onSuccess: (resp, action) => {
      setToast({ url: resp.commentUrl, action });
      setConfirmAction(null);
      queryClient.invalidateQueries({ queryKey: ['epic', String(epic.id)] });
    },
    onError: () => setConfirmAction(null),
  });

  // Close the "More" menu on outside click or Escape — same behaviour as
  // Header.tsx's NavDropdown.
  useEffect(() => {
    if (!moreOpen) return;
    const onClick = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  const terminal =
    epic.state === 'COMPLETE' ||
    epic.state === 'ABANDONED' ||
    epic.state === 'ABORTED' ||
    epic.state === 'BUGFIX_COMPLETE' ||
    epic.state === 'READY_FOR_REVIEW_NOTIFIED';

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setConfirmAction('retry')}
        disabled={mutation.isPending}
        data-testid="action-retry"
      >
        Retry
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setConfirmAction('proceed')}
        disabled={mutation.isPending || terminal}
        title={terminal ? 'Nothing is waiting for approval' : undefined}
        data-testid="action-proceed"
      >
        Proceed
      </Button>
      <Button
        variant="danger"
        size="sm"
        onClick={() => setConfirmAction('abort')}
        disabled={mutation.isPending || terminal}
        data-testid="action-abort"
        title={terminal ? `Already ${epic.state}` : 'Stop this run'}
      >
        Abort
      </Button>

      {isReportableEpic && (
        <div className="relative" ref={moreRef} data-testid="epic-actions-more">
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            data-testid="action-more"
            className="px-2.5 py-1.5 rounded-md text-sm inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors duration-150"
          >
            More <span aria-hidden className="text-slate-400 text-xs">▾</span>
          </button>
          {moreOpen && (
            <div
              role="menu"
              aria-label="More actions"
              className="absolute left-0 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMoreOpen(false);
                  setReportResult(null);
                  reportMutation.mutate();
                }}
                disabled={reportMutation.isPending}
                data-testid="action-assessment-report"
                className="w-full text-left block px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
                title="Render the Forge Assessment as a PDF/HTML pack — always human-review before sending to the customer"
              >
                {reportMutation.isPending
                  ? 'Generating report… (up to ~2 min)'
                  : reportResult ? 'Regenerate report' : 'Generate PDF Report'}
              </button>
            </div>
          )}
        </div>
      )}

      {reportResult && (
        <div
          role="status"
          className="text-xs text-success-700 bg-success-50 border border-success-100 rounded-md px-2.5 py-1 ml-1"
        >
          Report ready ·{' '}
          {reportResult.reportUrl ? (
            <a href={reportResult.reportUrl} target="_blank" rel="noreferrer" className="underline">
              download PDF ↗
            </a>
          ) : (
            <span>no PDF (Chromium unavailable) —</span>
          )}{' '}
          <a href={reportResult.htmlUrl} target="_blank" rel="noreferrer" className="underline">
            view HTML ↗
          </a>
          {' '}
          <button
            type="button"
            onClick={() => refreshLinkMutation.mutate()}
            disabled={refreshLinkMutation.isPending}
            data-testid="action-assessment-report-refresh-link"
            className="underline text-success-800 disabled:opacity-50"
          >
            {refreshLinkMutation.isPending ? 'refreshing…' : 'refresh link'}
          </button>
          {' '}<span className="text-success-800">(each link is valid 15 min — click "refresh link" any time to get a new one; always human-review before sending to the customer)</span>
        </div>
      )}
      {reportMutation.isError && (
        <div
          role="alert"
          className="text-xs text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-2.5 py-1 ml-1"
        >
          {(reportMutation.error as Error).message}
        </div>
      )}

      {toast && (
        <div
          role="status"
          className="text-xs text-success-700 bg-success-50 border border-success-100 rounded-md px-2.5 py-1 ml-1"
        >
          /{toast.action} posted ·{' '}
          <a
            href={toast.url}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            view comment ↗
          </a>
        </div>
      )}
      {mutation.isError && (
        <div
          role="alert"
          className="text-xs text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-2.5 py-1 ml-1"
        >
          {(mutation.error as Error).message}
        </div>
      )}

      {confirmAction && (
        <EpicConfirmDialog
          action={confirmAction}
          kind={epic.kind}
          onConfirm={() => mutation.mutate(confirmAction)}
          onCancel={() => setConfirmAction(null)}
          pending={mutation.isPending}
        />
      )}
    </div>
  );
};

const EpicConfirmDialog: React.FC<{
  action: EpicAction;
  kind: EpicDetail['kind'];
  onConfirm: () => void;
  onCancel: () => void;
  pending: boolean;
}> = ({ action, kind, onConfirm, onCancel, pending }) => {
  const verb =
    action === 'abort' ? 'abort' : action === 'retry' ? 'retry' : 'proceed';
  const target = kind === 'bugfix' ? 'bug fix' : 'epic';
  return (
    <SharedConfirmDialog
      open
      title={
        verb === 'abort'
          ? `Abort this ${target}?`
          : verb === 'retry'
          ? `Retry this ${target}?`
          : `Proceed with this ${target}?`
      }
      description={
        <>
          This posts <code className="font-mono">/{verb}</code> as a comment on
          the GitHub issue. The orchestrator picks it up on the next webhook tick.
        </>
      }
      confirmLabel={pending ? 'Sending…' : `Yes, ${verb}`}
      tone={verb === 'abort' ? 'danger' : 'primary'}
      pending={pending}
      onConfirm={onConfirm}
      onCancel={onCancel}
      testId={`confirm-${verb}`}
    />
  );
};
